import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getManifest } from '@rideiq/shared/api/services/Manifest';

// How often to re-check the manifest on navigation (avoids a request on every single route change)
const RECHECK_INTERVAL_MS = 5 * 60 * 1000;
const LAST_CHECKED_KEY = 'navGuardLastChecked';

// Silently hard-navigates to the new route when the bundle is stale, ensuring the
// user loads fresh chunks instead of triggering a ChunkLoadError mid-navigation.
export const useNavigationGuard = () => {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    // Use sessionStorage so the throttle survives page reloads — a module-level
    // variable resets to null on every reload, which would cause an infinite
    // redirect loop after a stale-bundle reload triggers a fresh page load.
    const lastChecked = sessionStorage.getItem(LAST_CHECKED_KEY);
    const now = Date.now();

    if (lastChecked && now - parseInt(lastChecked, 10) < RECHECK_INTERVAL_MS) {
      return;
    }

    sessionStorage.setItem(LAST_CHECKED_KEY, String(now));

    getManifest()
      .then((response) => {
        if (!response.data?.rideiqJs?.includes(__webpack_hash__)) {
          // Use window.location at resolve time rather than the captured location
          // ref to avoid redirecting to a stale route if the user navigated again
          // before this promise resolved.
          window.location.href = window.location.pathname + window.location.search + window.location.hash;
        }
      })
      .catch(() => {
        // Silently fail — a failed manifest check should never block navigation
      });
  }, [location.pathname, location.search, location.hash]);
};
